import { getIndexName } from '../../../../../constants';
import { generateUrlFromIndexUiState } from '../../../../../utils/search-router/url-handling';
import { LOCALIZED_PATHS } from '../../../../../utils/constants/url-constants';
import { buildLocationSrpPath } from '../../../../../utils/location/build-location-srp-path';
import { findRoleForAnyLocaleTitle, findRoleForNormalisedTitle } from '../../../../../utils/role/find-role-for-title';
import { buildRoleHubPath, resolveRoleHubSlug } from '../../../../../utils/role/role-hub-config';

type SupportedLocale = 'en' | 'fi' | 'sv';

function toSupportedLocale(locale: string): SupportedLocale {
  const normalized = locale.toLowerCase();
  if (normalized === 'fi' || normalized === 'sv') return normalized;
  return 'en';
}

/**
 * "See all similar jobs" target for the job detail page.
 *
 * When the job's normalised title is one of the configured role hubs, link to the indexable
 * role hub (`/jobs/role/{slug}`), or to the role x city page (`/jobs/location/{city}/role/{slug}`)
 * when the job has a location. This mirrors the company profile's top-role links
 * (utils/company/build-company-top-links.ts). The URL generator only *keeps* a `/role/{slug}`
 * segment that is already in the path and never derives one from a `normalisedTitle` refinement,
 * so without this branch the link would land on the noindex `?normalisedTitle=` filter URL.
 *
 * Titles that have no role hub fall back to a free-text search for the title, mirroring the
 * "similar jobs" rail's own query (fetchSimilarJobsFromAlgolia / buildSimilarJobsTitleFilter):
 * title-only, no location AND'd in. An exact `normalisedTitle` facet refinement was tried here
 * before, but it round-trips through generateUrlFromIndexUiState's lowercase-then-recapitalize
 * URL encoding, which mangles acronyms ("IT" -> "It") and lowercase connectors ("Head of Product"
 * -> "Head Of Product") into a value that no longer matches the facet, and it ANDed an exact
 * location match the rail never applies — so a rail full of hits could still land on a zero-result
 * search. Free text isn't lowercased/recapitalized on the URL round-trip and needs no location.
 */
export function buildSimilarJobsSrpHref(params: {
  locale: string;
  normalisedTitle?: string | null;
  primaryLocation?: string | null;
}): string {
  const locale = toSupportedLocale(params.locale);
  const basePath = `/${locale}/${LOCALIZED_PATHS[locale].jobs}`;
  const title = (params.normalisedTitle ?? '').trim();
  if (!title) return basePath;

  const location = (params.primaryLocation ?? '').trim();

  // The job page's normalisedTitle is in the page locale's language; try that first, then any
  // locale (the same fallback the company profile uses for titles of unknown language).
  const role = findRoleForNormalisedTitle(title, locale) ?? findRoleForAnyLocaleTitle(title);
  if (role) {
    const roleUrlSlug = resolveRoleHubSlug(role, locale);
    return location
      ? `/${locale}${buildLocationSrpPath(location, locale, undefined, roleUrlSlug)}`
      : buildRoleHubPath(roleUrlSlug, locale);
  }

  return generateUrlFromIndexUiState(
    { query: title },
    getIndexName(locale),
    basePath,
  );
}
